import { html } from 'lighterhtml';
import {
  useMemo,
  useRef,
  useEffect,
  useState,
  useReducer,
} from 'haunted/lib/core';
import mitt from 'mitt';
import HTMLParsedElement from 'html-parsed-element';

import { SliderEvent, SliderContextValue } from './slider.context';
import { component } from '../../utils';

import './slider.css';
import { useInterval } from '../../hooks/use-interval';
import { HTMLParsedChildrenElement } from '../../utils/html-children-parsed';

type SliderState = 'paused' | 'play';

/**
 * Checks if a manual action (like manually navigation) happened in the slider
 * which causes the auto slideshow to stop
 *
 * @param state
 * @param eventKey
 */
function autoplayReducer(
  state: SliderState,
  eventKey: keyof SliderEvent
): SliderState {
  switch (eventKey) {
    case 'moveToSlide':
    case 'nextSlide':
    case 'prevSlide':
    case 'keenDragStart':
      return 'paused';

    default:
      return state;
  }
}

function generateInitialState(hasTimer: boolean): SliderState {
  return hasTimer ? 'play' : 'paused';
}

function Slider(el: HTMLParsedChildrenElement) {
  const timer: null | number = useMemo(() => {
    try {
      if (typeof el.dataset.timer === 'string') {
        const parsedTimer = parseInt(el.dataset.timer, 10);
        if (!isNaN(parsedTimer) && parsedTimer > 0) {
          return parsedTimer;
        }
      }
    } catch {
      return null;
    }

    return null;
  }, [el.dataset.timer]);
  const [state, send] = useReducer(
    autoplayReducer,
    timer !== null,
    generateInitialState
  );
  const [numberOfSlides, setNumberOfSlides] = useState(0);
  const initialSlide = 0;
  const emitterRef = useRef(mitt<SliderEvent>());

  useEffect(() => {
    if (el.childrenParsed) {
      // Get number of slides
      const _numberOfSlides = el.querySelectorAll('.keen-slider__slide').length;
      setNumberOfSlides(_numberOfSlides);
    }
  }, [el.childrenParsed]);

  useEffect(() => {
    function eventChangeHandler(eventName: keyof SliderEvent) {
      send(eventName);
    }

    emitterRef.current.on('*', eventChangeHandler);

    return () => {
      emitterRef.current.off('*', eventChangeHandler);
    };
  }, [send]);

  useInterval(
    () => {
      emitterRef.current.emit('autoNextSlide');
    },
    state === 'play' ? timer : null
  );

  const contextValue: SliderContextValue = {
    emitterRef,
    initialSlide,
    numberOfSlides,
    state,
  };

  return html`<eo-slider-internal-provider value=${contextValue}>
    <slot></slot>
  </eo-slider-internal-provider>`;
}

const SliderElement = HTMLParsedElement.withParsedCallback(
  // @ts-ignore
  component(Slider, {
    baseElement: HTMLParsedChildrenElement,
  })
);

export { SliderElement };
